import { useAtom } from 'jotai/index';
import { Toast } from 'primereact/toast';
import { useCallback } from 'react';
import {
  endpointDocumentClauseGET,
  endpointDocumentFieldGET,
} from '~/api/def/endpoints/document';
import { endpointExtractionFieldGET } from '~/api/def/endpoints/extractionFieldEndpoint';
import { useDocumentCache } from '~/atoms/document/useDocumentCache';
import { toastAtom } from '~/atoms/toastAtom';
import {
  APIClauseToPDFClause,
  APIFieldToPDFField,
} from '~/lib/pdfViewer/api2pdf';
import { SourceBoundingBox } from '~/lib/pdfViewer/types';

import { useAtomValue } from 'jotai';
import { v7 as idv7 } from 'uuid';
import { endpointFundDocumentUrlGET } from '~/api/def/endpoints/document';
import { pcEvaluationEventPOST } from '~/api/def/endpoints/pcValuationEventEndpoint';
import {
  clausesAtom,
  documentFieldsAtom,
  documentMetaAtom,
  documentUrlAtom,
  fieldTypesAtom,
  globalSearchPdfFieldAtom,
  loadingClausesAtom,
  loadingDocumentMetaAtom,
  loadingDocumentUrlAtom,
  loadingFieldsAtom,
  pdfClausesAtom,
  pdfFieldsAtom,
  pdfFieldsMergedAtom,
} from '~/atoms/document/documentOverviewAtoms';

export const useDocumentOverviewApi = ({
  documentId,
}: {
  documentId?: string;
}) => {
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const [clauses, setClauses] = useAtom(clausesAtom);
  const [pdfClauses, setPdfClauses] = useAtom(pdfClausesAtom);
  const [_pdfFields, setPdfFields] = useAtom(pdfFieldsAtom);
  const [documentFields, setDocumentFields] = useAtom(documentFieldsAtom);
  const [documentMeta, setDocumentMeta] = useAtom(documentMetaAtom);
  const [fieldTypes, setFieldTypes] = useAtom(fieldTypesAtom);
  const [documentUrl, setDocumentUrl] = useAtom(documentUrlAtom);
  const [loadingFields, setLoadingFields] = useAtom(loadingFieldsAtom);
  const [loadingClauses, setLoadingClauses] = useAtom(loadingClausesAtom);
  const [globalSearchPdfField, setGlobalSearchPdfField] = useAtom(
    globalSearchPdfFieldAtom,
  );
  const mergedPdfFields = useAtomValue(pdfFieldsMergedAtom);
  const [loadingDocumentMeta, setLoadingDocumentMeta] = useAtom(
    loadingDocumentMetaAtom,
  );
  const [loadingDocumentUrl, setLoadingDocumentUrl] = useAtom(
    loadingDocumentUrlAtom,
  );
  const { getDocument } = useDocumentCache();

  const fetchFieldTypes = useCallback(async () => {
    const fieldTypesResponse = await endpointExtractionFieldGET();
    if (fieldTypesResponse?.success === 1) {
      setFieldTypes(
        fieldTypesResponse.data.map((f) => {
          return {
            ...f,
            id: f.id,
          };
        }),
      );
    }
  }, []);

  const addSearchMatchField = useCallback(
    (box: SourceBoundingBox) => {
      setGlobalSearchPdfField({
        fieldId: idv7(),
        field: 'Global Search Match',
        value: '',
        sourceBoundingBox: box,
      });
    },
    [setGlobalSearchPdfField],
  );

  const fetchDocumentUrl = useCallback(async () => {
    if (!documentId) return;
    setLoadingDocumentUrl(true);
    const documentResponse = await endpointFundDocumentUrlGET(documentId);
    setLoadingDocumentUrl(false);
    if (documentResponse?.success === 1) {
      setDocumentUrl(documentResponse.data.presignedUrl);
    } else {
      if (toastCurrent) {
        toastCurrent.show({
          severity: 'error',
          summary: 'Fetching Document url failed',
          detail: documentResponse?.message || 'Unknown status',
        });
      }
    }
  }, [documentId, setDocumentUrl, setLoadingDocumentUrl, toastCurrent]);

  const getDocumentMeta = useCallback(async () => {
    if (!documentId) return;
    setLoadingDocumentMeta(true);
    const meta = await getDocument(documentId);

    setLoadingDocumentMeta(false);
    if (meta) {
      setDocumentMeta(meta);
    }
  }, [documentId, getDocument, setDocumentMeta, setLoadingDocumentMeta]);

  const fetchDocumentFields = useCallback(
    async (id = documentId) => {
      if (!id) return;
      setLoadingFields(true);
      const documentFieldsResponse = await endpointDocumentFieldGET(id);
      setLoadingFields(false);
      if (documentFieldsResponse?.success === 1) {
        setDocumentFields(documentFieldsResponse.data);
        setPdfFields(
          documentFieldsResponse.data.map((f) => APIFieldToPDFField(f)),
        );
      }
    },
    [documentId, setDocumentFields, setPdfFields, setLoadingFields],
  );

  const fetchClauses = useCallback(async () => {
    if (!documentId) return;
    setLoadingClauses(true);

    const clausesResponse = await endpointDocumentClauseGET(documentId);
    setLoadingClauses(false);
    if (clausesResponse?.success === 1) {
      setClauses(clausesResponse.data);
      setPdfClauses(clauses.map((c) => APIClauseToPDFClause(c)));
    }
  }, [documentId, setClauses, setPdfClauses, clauses, setLoadingClauses]);

  const loadDocument = useCallback(() => {
    if (!documentId) {
      return;
    }

    fetchDocumentUrl();
    fetchDocumentFields();
    fetchClauses();
    fetchFieldTypes();
    getDocumentMeta();
  }, [
    documentId,
    fetchDocumentUrl,
    fetchDocumentFields,
    fetchClauses,
    fetchFieldTypes,
    getDocumentMeta,
  ]);

  const createPcValuationEvent = (portfolioCompanyId: string) => {
    // Todo: Toast not working.
    if (!documentId) return;
    const extractedFieldIds = documentFields.map(
      (extractedField) => extractedField.id,
    );
    pcEvaluationEventPOST({
      portfolioCompanyId: portfolioCompanyId,
      extractedFieldIds: extractedFieldIds,
      documentId: documentId,
    })
      .then((res) => {
        if (res && res.response === 200) {
          toastCurrent?.show({
            severity: 'success',
            summary: 'Evaluation event created successfully',
          });
        } else {
          toastCurrent?.show({
            severity: 'error',
            summary: 'Something went wrong',
          });
        }
      })
      .catch(() => {
        toastCurrent?.show({
          severity: 'error',
          summary: 'Something went wrong',
        });
      });
  };

  return {
    documentFields,
    pdfClauses,
    pdfFields: mergedPdfFields,
    clauses,
    fieldTypes,
    documentUrl,
    fetchDocumentFields,
    fetchClauses,
    loadingClauses,
    loadingFields,
    loadingDocumentMeta,
    loadingDocumentUrl,
    documentMeta,
    loadDocument,
    addSearchMatchField,
    globalSearchPdfField,
    createPcValuationEvent,
  };
};
